import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from '../utils/axiosInstance';

/**
 * Shared hook for metafield definitions list data, sync status, and search filtering.
 * @param {'PRODUCT' | 'PRODUCTVARIANT'} ownerType
 * @returns {{ definitions: Array, filteredDefinitions: Array, loading: boolean, error: string|null, search: string, setSearch: function, syncModalOpen: boolean, setSyncModalOpen: function, lastSynced: string|null, fetchData: function }}
 */
export default function useMetafieldDefinitions(ownerType) {
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);

  // Debounce search input (300ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [defsRes, syncRes] = await Promise.all([
        axios.get(`/api/transly/metafields/definitions/`, {
          params: { owner_type: ownerType },
        }),
        axios.get(`/api/transly/metafields/sync/`),
      ]);
      setDefinitions(defsRes.data.definitions || []);
      setLastSynced(syncRes.data.last_completed || null);
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || 'Failed to load definitions'
      );
    } finally {
      setLoading(false);
    }
  }, [ownerType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Client-side filtering by namespace, key, or type_name
  const filteredDefinitions = useMemo(() => {
    if (!debouncedSearch) return definitions;
    const term = debouncedSearch.toLowerCase();
    return definitions.filter(
      (d) =>
        (d.namespace || '').toLowerCase().includes(term) ||
        (d.key || '').toLowerCase().includes(term) ||
        (d.type_name || '').toLowerCase().includes(term)
    );
  }, [definitions, debouncedSearch]);

  return {
    definitions,
    filteredDefinitions,
    loading,
    error,
    search,
    setSearch,
    syncModalOpen,
    setSyncModalOpen,
    lastSynced,
    fetchData,
  };
}
